import { useInfiniteQuery } from "@tanstack/react-query";
import { restaurantService } from "../api/services/restaurantService";
import type {
  Restaurant,
  RestaurantFilters,
  RestaurantResult,
} from "../schemas/restaurantSchema";
import { useRestaurants } from "./useRestaurants";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type RestaurantsInfiniteFilters = Omit<
  RestaurantFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useRestaurantsInfinite = (
  filters: RestaurantsInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useRestaurants(undefined, { enabled: false });

  const query = useInfiniteQuery<RestaurantResult, Error>({
    queryKey: ["restaurants", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      restaurantService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const restaurants: Restaurant[] =
    query.data?.pages.flatMap((p) => p.restaurants) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    restaurants,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
